import { cn } from "@/utils";
import { ChevronDown, Globe } from "lucide-react";
import { useState, useTransition } from "react";
import { useTranslation } from "react-i18next";
import { Modal, ModalBody, ModalContent, ModalHeader } from "@heroui/react";
import { AnimatePresence, motion } from "framer-motion";
import { trackLanguageChange } from "@/utils/analytics";

const LOCALE_STORAGE_KEY = "uno_locale_v1";
const LANGUAGE_OPTIONS = [
  { code: "ar", label: "Arabic", flag: "/iq-flag.svg", alt: "Iraq Flag" },
  { code: "en", label: "English", flag: "/us-flag.svg", alt: "US Flag" },
  { code: "ku", label: "Kurdish", flag: "/ku-flag.svg", alt: "Kurdistan Flag" },
] as const;

export const LanguageDropDown = ({
  variant = "navbar",
  onClose,
  isTransparent = false,
  direction = "down",
  noBorder = false,
  noBg = false,
}: {
  variant?: "navbar" | "sidebar" | "minimal" | "pill";
  onClose?: () => void;
  isTransparent?: boolean;
  direction?: "up" | "down";
  noBorder?: boolean;
  noBg?: boolean;
}) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [isPending, startTransition] = useTransition();
  const { t, i18n } = useTranslation();
  const locale = i18n.language;

  function onSelectChange(lang: string) {
    if (lang === locale) {
      setIsOpen(false);
      return;
    }
    trackLanguageChange(lang, locale);
    startTransition(() => {
      if (typeof window !== "undefined") {
        try {
          // Save language preference
          window.localStorage.setItem(LOCALE_STORAGE_KEY, lang);
          // Mark session so the AppLoader won't re-show language picker
          window.sessionStorage.setItem("uno_loader_shown", "true");
        } catch {
          // ignore
        }
      }
      i18n.changeLanguage(lang);
    });
    setIsOpen(false);
    if (variant === "sidebar") onClose?.();
  }

  const buttonStyles =
    variant === "sidebar"
      ? "flex items-center gap-2 w-full px-3 py-2 cursor-pointer bg-bodyBg/70 hover:bg-secondaryText hover:dark:bg-bodyBg rounded-lg primary-transition"
      : variant === "minimal"
        ? `size-9 lg:size-10 rounded-lg ${noBorder ? "" : "border"} hover:shadow-sm transition-all duration-200 flex items-center justify-center ${
            isTransparent
              ? "bg-white/8 text-[#EAE4DB] hover:bg-white/14 " + (noBorder ? "" : "border-white/12")
              : "bg-white text-primary hover:bg-primary/5 " + (noBorder ? "" : "border-primary/15")
          }`
        : "flex items-center gap-1.5 px-4 py-2 cursor-pointer border border-border-1 rounded-[50px] transition-all duration-200 hover:bg-default-100 dark:hover:bg-mainBg/5 hover:border-secondary/40";

  if (variant === "minimal") {
    return (
      <div className="relative">
        <button
          className={buttonStyles}
          onClick={() => setIsOpen(!isOpen)}
          aria-label={t("KEY.LANGUAGE", "Select Language")}
        >
          <Globe className="size-4" />
        </button>

        <AnimatePresence>
          {isOpen && (
            <>
              {/* Backdrop */}
              <div
                className="fixed inset-0 z-40"
                onClick={() => setIsOpen(false)}
              />
              <motion.div
                initial={{ opacity: 0, y: -5, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -5, scale: 0.95 }}
                transition={{ type: "spring", stiffness: 400, damping: 25 }}
                className="absolute top-[calc(100%+8px)] end-0 z-[101] mt-2 bg-mainBg border border-primary/10 rounded-xl shadow-xl min-w-[160px] overflow-hidden origin-top-right"
              >
                <ul className="flex flex-col p-1.5" role="menu">
                  {LANGUAGE_OPTIONS.map((lang) => {
                    const isActive = locale === lang.code;
                    return (
                      <li
                        key={lang.code}
                        className={cn(
                          "flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200",
                          isActive
                            ? "bg-primary/10 cursor-default"
                            : "cursor-pointer hover:bg-primary/5",
                        )}
                        onClick={() => onSelectChange(lang.code)}
                      >
                        <img
                          src={lang.flag}
                          alt={lang.alt}
                          width={24}
                          height={16}
                          className="rounded-sm shrink-0 shadow-sm"
                          onError={(e) => {
                            (e.target as HTMLImageElement).style.display = "none";
                          }}
                        />
                        <span
                          className={cn(
                            "text-sm",
                            isActive
                              ? "text-primary font-semibold"
                              : "text-primaryText",
                          )}
                        >
                          {lang.label}
                        </span>
                        {isActive && (
                          <span className="ms-auto text-primary">✓</span>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </motion.div>
            </>
          )}
        </AnimatePresence>
      </div>
    );
  }

  if (variant === "pill") {
    const activeLang = LANGUAGE_OPTIONS.find((lang) => lang.code === locale) || LANGUAGE_OPTIONS[0];

    return (
      <div className="relative">
        <button
          className={cn(
            "group relative flex items-center gap-1.5 pl-1.5 pr-2.5 rtl:pr-1.5 rtl:pl-2.5 py-1.5 rounded-full transition-all duration-300",
            noBorder ? "border-0 shadow-none" : "border",
            noBg
              ? "bg-transparent hover:bg-black/5 dark:hover:bg-white/10 shadow-none"
              : isTransparent
                ? "bg-black/20 hover:bg-black/30 shadow-[0_4px_12px_rgba(0,0,0,0.1)] backdrop-blur-md"
                : "bg-[#F4EFEA] hover:bg-[#EAE4DB] shadow-[0_2px_8px_rgba(0,0,0,0.04)] backdrop-blur-md",
            !noBorder && (isTransparent ? "border-white/10" : "border-[#E5DEC9]")
          )}
          onClick={() => setIsOpen(!isOpen)}
          aria-label={t("KEY.LANGUAGE", "Select Language")}
        >
          <div className="relative flex items-center justify-center w-5 h-5 rounded-full overflow-hidden border border-black/5 shadow-sm shrink-0">
            <img 
              src={activeLang.flag} 
              alt={activeLang.alt}
              className="w-full h-full object-cover"
              onError={(e) => {
                (e.target as HTMLImageElement).style.display = "none";
              }}
            />
          </div>
          <span className={cn(
            "text-[10px] font-bold uppercase tracking-widest",
            isTransparent ? "text-white" : "text-[#1E1E1E]"
          )}>
            {activeLang.code}
          </span>
          <ChevronDown className={cn(
            "w-3 h-3 transition-transform duration-300",
            isOpen && (direction === "up" ? "-rotate-180" : "rotate-180"),
            direction === "up" && "rotate-180",
            isTransparent ? "text-white/70" : "text-[#8B7E66]"
          )} />
        </button>

        <AnimatePresence>
          {isOpen && (
            <>
              {/* Backdrop */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="fixed inset-0 z-[100]"
                onClick={() => setIsOpen(false)}
              />
              
              {/* Dropdown Menu */}
              <motion.div
                initial={{ opacity: 0, y: direction === "up" ? 5 : -5, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: direction === "up" ? 5 : -5, scale: 0.95 }}
                transition={{ type: "spring", stiffness: 400, damping: 25 }}
                className={cn(
                  "absolute end-0 z-[101] min-w-[130px] p-1.5 rounded-2xl border backdrop-blur-xl shadow-[0_12px_40px_rgba(0,0,0,0.12)] overflow-hidden",
                  direction === "up" ? "bottom-[calc(100%+8px)] origin-bottom-right" : "top-[calc(100%+8px)] origin-top-right",
                  isTransparent 
                    ? "bg-[#1E1E1E]/80 border-white/10" 
                    : "bg-[#F4EFEA]/95 border-[#E5DEC9]"
                )}
              >
                <div className="flex flex-col gap-0.5">
                  {LANGUAGE_OPTIONS.map((lang) => {
                    const isActive = locale === lang.code;
                    return (
                      <button
                        key={lang.code}
                        onClick={() => onSelectChange(lang.code)}
                        className={cn(
                          "flex items-center gap-2.5 px-2.5 py-2 rounded-xl transition-all duration-200 text-left w-full",
                          isActive
                            ? (isTransparent ? "bg-white/10" : "bg-white border border-[#DCD3B9]/50 shadow-sm")
                            : (isTransparent ? "hover:bg-white/5" : "hover:bg-white/60")
                        )}
                      >
                        <div className="w-5 h-5 rounded-full overflow-hidden border border-black/5 shadow-sm shrink-0">
                          <img
                            src={lang.flag}
                            alt={lang.alt}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              (e.target as HTMLImageElement).style.display = "none";
                            }}
                          />
                        </div>
                        <span
                          className={cn(
                            "text-xs font-semibold flex-1",
                            isActive 
                              ? (isTransparent ? "text-white" : "text-[#4E82B4]")
                              : (isTransparent ? "text-white/70" : "text-[#8B7E66]")
                          )}
                        >
                          {lang.label}
                        </span>
                        {isActive && (
                          <div className="w-1.5 h-1.5 rounded-full bg-[#4E82B4] shrink-0" />
                        )}
                      </button>
                    );
                  })}
                </div>
              </motion.div>
            </>
          )}
        </AnimatePresence>
      </div>
    );
  }

  if (variant === "sidebar") {
    const activeLang =
      LANGUAGE_OPTIONS.find((lang) => lang.code === locale) ??
      LANGUAGE_OPTIONS[0];

    return (
      <>
        <button
          type="button"
          className={buttonStyles}
          onClick={() => setIsOpen(true)}
        >
          <img
            src={activeLang.flag}
            alt={activeLang.alt}
            width={28}
            height={16}
            className="rounded-sm min-w-7 min-h-4"
            onError={(e) => {
              (e.target as HTMLImageElement).style.display = "none";
            }}
          />
          <span className="text-primary bodyText max-sm:text-xs capitalize">
            {activeLang.label}
          </span>
        </button>

        <Modal
          isOpen={isOpen}
          onClose={() => setIsOpen(false)}
          size="sm"
          placement="center"
        >
          <ModalContent>
            {() => (
              <>
                <ModalHeader className="flex items-center justify-between">
                  <span className="h5Text text-primary">
                    {t("KEY.LANGUAGE", "Select Language")}
                  </span>
                </ModalHeader>
                <ModalBody>
                  <div className="flex flex-col gap-2 pb-2 px-1">
                    {LANGUAGE_OPTIONS.map((lang) => {
                      const isSelected = locale === lang.code;
                      return (
                        <button
                          key={lang.code}
                          type="button"
                          className={cn(
                            "w-full cursor-pointer flex items-center justify-between gap-3 rounded-xl border px-3 py-2 text-left hover:bg-default-100 dark:hover:bg-bodyBg/40",
                            isSelected
                              ? "border-secondary ring-2 ring-secondary/40 bg-default-100 dark:bg-bodyBg/50"
                              : "border-divider",
                          )}
                          onClick={() => onSelectChange(lang.code)}
                        >
                          <div className="flex items-center gap-3 min-w-0">
                            <img
                              src={lang.flag}
                              alt={lang.code}
                              width={20}
                              height={15}
                              className="rounded-sm"
                              onError={(e) => {
                                (e.target as HTMLImageElement).style.display =
                                  "none";
                              }}
                            />
                            <span
                              className={cn(
                                "bodyText truncate",
                                isSelected
                                  ? "text-primary"
                                  : "text-primaryText",
                              )}
                            >
                              {lang.label}
                            </span>
                          </div>

                          <span
                            className={cn(
                              "h6Text shrink-0 uppercase",
                              isSelected ? "text-primary" : "text-thirdText",
                            )}
                          >
                            {lang.code}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </ModalBody>
              </>
            )}
          </ModalContent>
        </Modal>
      </>
    );
  }

  return (
    <div className="relative">
      <button className={buttonStyles} onClick={() => setIsOpen(!isOpen)}>
        <Globe className="size-4" />
        <span className="text-sm text-primaryText">
          {LANGUAGE_OPTIONS.find((lang) => lang.code === locale)?.label}
        </span>
        <ChevronDown size={16} />
      </button>

      {isOpen && (
        <div className="absolute top-full right-0 mt-1 bg-mainBg border border-border-2 rounded-lg shadow-lg z-50 min-w-[150px]">
          <ul className="flex flex-col gap-1 p-1" role="menu">
            {LANGUAGE_OPTIONS.map((lang) => {
              const isActive = locale === lang.code;
              return (
                <li
                  key={lang.code}
                  className={cn(
                    "flex items-center gap-2.5 px-3 py-2 rounded-lg transition-colors duration-150",
                    isActive
                      ? "bg-secondary/10 cursor-default"
                      : "cursor-pointer hover:bg-default-100 dark:hover:bg-mainBg/5",
                  )}
                  onClick={() => onSelectChange(lang.code)}
                >
                  <img
                    src={lang.flag}
                    alt={lang.alt}
                    width={24}
                    height={16}
                    className="rounded-sm shrink-0"
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = "none";
                    }}
                  />
                  <span
                    className={cn(
                      "bodyText",
                      isActive
                        ? "text-secondary font-semibold"
                        : "text-primaryText",
                    )}
                  >
                    {lang.label}
                  </span>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
};
